import { useEffect, useRef } from 'react'
import { generalLog } from 'marketing/api'
import noop from 'lib/function/noop'

const STALL_TIMEOUT_MS = 15_000

function useMetaSearchStallWarning(
  propertyId: string | undefined,
  region: string,
  offerList: Pick<App.OfferList, 'searchId' | 'offerIds' | 'streamEnded' | 'metaSearchStream'>,
  enabled: boolean,
) {
  const searchId = offerList.searchId
  const offerCount = offerList.offerIds.length
  const requestedOfferCount = offerList.metaSearchStream?.requestedOfferCount
  const isWaiting = enabled && !offerList.streamEnded && requestedOfferCount !== undefined && offerCount < requestedOfferCount
  const reportedRequestRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isWaiting || !searchId) return
    const requestKey = `${searchId}:${requestedOfferCount}`
    if (reportedRequestRef.current === requestKey) return

    // Candidate responses alone do not reset the timer: only usable offers
    // making progress should keep a waiting batch from being reported.
    const timeout = setTimeout(() => {
      if (document.visibilityState === 'hidden') return
      reportedRequestRef.current = requestKey
      generalLog({
        status: 'warn',
        message: 'Metasearch results stalled',
        payload: {
          event: 'metasearch_results_stalled',
          propertyId,
          region,
          searchId,
          offerCount,
          requestedOfferCount,
          timeoutMs: STALL_TIMEOUT_MS,
        },
      }).catch(noop)
    }, STALL_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [isWaiting, searchId, requestedOfferCount, offerCount, propertyId, region])
}

export default useMetaSearchStallWarning
